import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { VendaEtapa, VendaSessao, VendaAnalise, VendaTrecho } from "@/lib/vendaAssistant";

type AuxiliarVendasState = {
  loading: boolean;
  saving: boolean;
  error: string | null;
  empresaId: string | null;
  sessoes: VendaSessao[];
  sessaoAtiva: VendaSessao | null;
  trechos: VendaTrecho[];
  analises: VendaAnalise[];
  usuariosMap: Record<string, { nome: string; email: string }>;
};

const initialState: AuxiliarVendasState = {
  loading: true,
  saving: false,
  error: null,
  empresaId: null,
  sessoes: [],
  sessaoAtiva: null,
  trechos: [],
  analises: [],
  usuariosMap: {},
};

export function useAuxiliarVendas(sessaoId?: string) {
  const [state, setState] = useState<AuxiliarVendasState>(initialState);

  const carregarEmpresaEUsuarios = useCallback(async (userId: string) => {
    const { data: usuario, error: userErr } = await (supabase as any)
      .from("usuarios")
      .select("empresa_id")
      .eq("id", userId)
      .single();

    if (userErr || !usuario?.empresa_id) {
      setState((prev) => ({ ...prev, loading: false, error: "Empresa não vinculada ao usuário." }));
      return null;
    }

    const empId = usuario.empresa_id;

    // Buscar lista de usuários da mesma empresa em consulta separada (sem JOIN)
    const { data: usuariosEmpresa } = await (supabase as any)
      .from("usuarios")
      .select("id, nome, email")
      .eq("empresa_id", empId);

    const uMap: Record<string, { nome: string; email: string }> = {};
    (usuariosEmpresa || []).forEach((u: any) => {
      uMap[u.id] = { nome: u.nome || u.email, email: u.email };
    });

    setState((prev) => ({ ...prev, empresaId: empId, usuariosMap: uMap }));
    return empId;
  }, []);

  const carregarListaSessoes = useCallback(async (empId: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    const { data, error } = await (supabase as any)
      .from("venda_sessoes")
      .select("*")
      .eq("empresa_id", empId)
      .order("ultima_interacao_em", { ascending: false });

    if (error) {
      setState((prev) => ({ ...prev, loading: false, error: error.message }));
      return;
    }

    setState((prev) => ({
      ...prev,
      sessoes: (data || []) as VendaSessao[],
      loading: false,
    }));
  }, []);

  const carregarDetalhesSessao = useCallback(async (sId: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    // Consulta 1: Sessão
    const { data: sessao, error: sErr } = await (supabase as any)
      .from("venda_sessoes")
      .select("*")
      .eq("id", sId)
      .single();

    if (sErr || !sessao) {
      setState((prev) => ({ ...prev, loading: false, error: "Sessão não encontrada." }));
      return;
    }

    // Consulta 2 (separada, sem JOIN): Trechos da conversa
    const { data: trechosData } = await (supabase as any)
      .from("venda_trechos")
      .select("*")
      .eq("sessao_id", sId)
      .order("created_at", { ascending: false });

    // Consulta 3 (separada, sem JOIN): Análises da sessão
    const { data: analisesData } = await (supabase as any)
      .from("venda_analises")
      .select("*")
      .eq("sessao_id", sId)
      .order("created_at", { ascending: false });

    setState((prev) => ({
      ...prev,
      sessaoAtiva: sessao as VendaSessao,
      trechos: (trechosData || []) as VendaTrecho[],
      analises: (analisesData || []) as VendaAnalise[],
      loading: false,
    }));
  }, []);

  const criarSessao = async (dados: { cliente: string; oportunidade?: string; observacoes?: string }, userId: string) => {
    if (!state.empresaId || !dados.cliente.trim()) return null;

    setState((prev) => ({ ...prev, saving: true, error: null }));

    const { data, error } = await (supabase as any)
      .from("venda_sessoes")
      .insert({
        empresa_id: state.empresaId,
        criado_por: userId,
        responsavel_id: userId,
        cliente_nome: dados.cliente.trim(),
        oportunidade: dados.oportunidade?.trim() || null,
        observacoes: dados.observacoes?.trim() || null,
        etapa: "fazendo_contato",
        status: "ativa",
      })
      .select("*")
      .single();

    if (error || !data) {
      setState((prev) => ({ ...prev, saving: false, error: error?.message || "Não foi possível criar a sessão." }));
      return null;
    }

    const novaSessao = data as VendaSessao;
    setState((prev) => ({
      ...prev,
      saving: false,
      sessoes: [novaSessao, ...prev.sessoes],
    }));

    return novaSessao;
  };

  const moverEtapa = async (sId: string, novaEtapa: VendaEtapa) => {
    setState((prev) => ({ ...prev, saving: true, error: null }));

    // Executar RPC procedure criada na migração
    const { error } = await (supabase as any).rpc("mover_etapa_venda", {
      p_sessao_id: sId,
      p_nova_etapa: novaEtapa,
    });

    if (error) {
      // Fallback para update direto se RPC não estiver implantada no ambiente
      const { error: updErr } = await (supabase as any)
        .from("venda_sessoes")
        .update({ etapa: novaEtapa, ultima_interacao_em: new Date().toISOString() })
        .eq("id", sId);

      if (updErr) {
        setState((prev) => ({ ...prev, saving: false, error: updErr.message }));
        return false;
      }
    }

    setState((prev) => ({
      ...prev,
      saving: false,
      sessoes: prev.sessoes.map((s) => (s.id === sId ? { ...s, etapa: novaEtapa } : s)),
      sessaoAtiva: prev.sessaoAtiva?.id === sId ? { ...prev.sessaoAtiva, etapa: novaEtapa } : prev.sessaoAtiva,
    }));

    return true;
  };

  const recarregarSessao = useCallback(async () => {
    if (sessaoId) {
      await carregarDetalhesSessao(sessaoId);
    }
  }, [sessaoId, carregarDetalhesSessao]);

  return {
    state,
    setState,
    carregarEmpresaEUsuarios,
    carregarListaSessoes,
    carregarDetalhesSessao,
    criarSessao,
    moverEtapa,
    recarregarSessao,
  };
}
